import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

interface ExhibitNavigationData {
  prevExhibit: { id: string } | null;
  nextExhibit: { id: string } | null;
  tourId: string | null;
  loading: boolean;
}

export function useExhibitNavigation(currentExhibitId: string | undefined): ExhibitNavigationData {
  const location = useLocation();
  const [navigationData, setNavigationData] = useState<ExhibitNavigationData>({
    prevExhibit: null,
    nextExhibit: null,
    tourId: null,
    loading: false
  });

  useEffect(() => {
    if (!currentExhibitId) return;

    // Extract tour ID from location state
    const fromState = location.state?.from;
    let tourId: string | null = null;

    if (typeof fromState === 'string' && fromState.startsWith('tour-')) {
      tourId = fromState.replace('tour-', '');
    }

    if (!tourId) {
      setNavigationData({
        prevExhibit: null,
        nextExhibit: null,
        tourId: null,
        loading: false
      });
      return;
    }

    const fetchNavigationData = async () => {
      setNavigationData(prev => ({ ...prev, loading: true }));

      try {
        // Fetch tour exhibits ordered by sequence
        const { data: tourExhibits, error } = await supabase
          .from('tour_exhibits')
          .select('exhibit_id, sequence_order')
          .eq('tour_id', tourId)
          .order('sequence_order', { ascending: true });

        if (error) {
          console.error('Error fetching tour exhibits:', error);
          return;
        }

        if (!tourExhibits || tourExhibits.length === 0) {
          setNavigationData({
            prevExhibit: null,
            nextExhibit: null,
            tourId,
            loading: false
          });
          return;
        }

        // Find current exhibit index
        const currentIndex = tourExhibits.findIndex(
          exhibit => exhibit.exhibit_id === currentExhibitId
        );

        if (currentIndex === -1) {
          setNavigationData({
            prevExhibit: null,
            nextExhibit: null,
            tourId,
            loading: false
          });
          return;
        }

        // Determine previous and next exhibits
        const prevExhibit = currentIndex > 0 
          ? { id: tourExhibits[currentIndex - 1].exhibit_id }
          : null;

        const nextExhibit = currentIndex < tourExhibits.length - 1
          ? { id: tourExhibits[currentIndex + 1].exhibit_id }
          : null;

        setNavigationData({
          prevExhibit,
          nextExhibit,
          tourId,
          loading: false
        });

      } catch (error) {
        console.error('Error in exhibit navigation:', error);
        setNavigationData({
          prevExhibit: null,
          nextExhibit: null,
          tourId,
          loading: false
        });
      }
    };

    fetchNavigationData();
  }, [currentExhibitId, location.state]);

  return navigationData;
}